import { resolveId } from '@medplum/core'
import type {
  AllergyIntolerance,
  CarePlan,
  CodeableConcept,
  Coding,
  Communication,
  Composition,
  Condition,
  DocumentReference,
  Immunization,
  MedicationRequest,
  Observation,
  Patient,
  QuestionnaireResponse,
  Reference,
  ServiceRequest,
  Task,
} from '@medplum/fhirtypes'
import {
  codeFromCodeableConcept,
  getCodingFromCodeableConcept,
} from '@seen/core/codeableConcept'
import * as codeSystems from '@seen/core/codeSystems'
import { getCodingDetails, type ScaleType } from '@seen/core/codings'
import { formatDate } from '@seen/core/dates'
import { referenceDisplay } from '@seen/core/display'
import { featureFlags } from '@seen/core/featureFlags'
import { invariant } from '@seen/core/invariant'
import { ExtendedAllergy } from '@seen/core/models/allergies'
import { Intervention } from '@seen/core/models/care-plans/intervention'
import { CommentCommunication } from '@seen/core/models/communications/CommentCommunication'
import { Diagnosis } from '@seen/core/models/conditions/Diagnosis'
import { ExtendedDocumentReference } from '@seen/core/models/document-references/documentReferences'
import {
  BLOOD_PRESSURE_CODING,
  BLOOD_PRESSURE_SCALE_TYPE,
  BLOOD_PRESSURE_UNIT,
  BODY_HEIGHT_CODING,
  BODY_HEIGHT_SCALE_TYPE,
  BODY_HEIGHT_UNIT,
  BODY_MASS_INDEX_CODING,
  BODY_MASS_INDEX_SCALE_TYPE,
  BODY_MASS_INDEX_UNIT,
  BODY_TEMPERATURE_CODING,
  BODY_TEMPERATURE_SCALE_TYPE,
  BODY_TEMPERATURE_UNIT,
  BODY_WEIGHT_CODING,
  BODY_WEIGHT_SCALE_TYPE,
  BODY_WEIGHT_UNIT,
  HEART_RATE_CODING,
  HEART_RATE_SCALE_TYPE,
  HEART_RATE_UNIT,
  LAB_OPTIONS,
  OXYGEN_SATURATION_CODING,
  OXYGEN_SATURATION_SCALE_TYPE,
  OXYGEN_SATURATION_UNIT,
  PAIN_SEVERITY_ANSWER_OPTIONS,
  PAIN_SEVERITY_CODING,
  PAIN_SEVERITY_SCALE_TYPE,
  PAIN_SEVERITY_UNIT,
  RESPIRATORY_RATE_CODING,
  RESPIRATORY_RATE_SCALE_TYPE,
  RESPIRATORY_RATE_UNIT,
} from '@seen/core/models/loinc'
import { ExtendedMedicationRequest } from '@seen/core/models/medicationRequest'
import { NoteTemplate } from '@seen/core/models/noteTemplate'
import { ExtendedObservation } from '@seen/core/models/observation'
import { Order } from '@seen/core/models/orders/Order'
import { OrderTemplate } from '@seen/core/models/orderTemplate'
import { Participant } from '@seen/core/models/patients/participant'
import { ExtendedPractitioner } from '@seen/core/models/practitioner'
import * as questionnaires from '@seen/core/models/questionnaires/index'
import { ValidatedToolResponse } from '@seen/core/models/validatedToolResponse'
import { createReference, getReferenceString } from '@seen/core/references'
import { MentionNode as MentionSchema } from '@seen/core/tiptap/schema'
import { generateUUID, isOfType, parseReferenceString } from '@seen/core/utils'
import { IconCalendar } from '@seen/ui'
import {
  IconActivity,
  IconClipboardCheck,
  IconClipboardPlus,
  IconFile,
  IconFlask,
  IconListCheck,
  IconPill,
  IconPlus,
  IconSignature,
  IconSquareRoundedCheck,
  IconTemplate,
  IconUsersGroup,
  IconVirus,
} from '@tabler/icons-react'
import type { ResolvedPos } from '@tiptap/pm/model'
import {
  type NodeViewProps,
  NodeViewWrapper,
  nodePasteRule,
  ReactNodeViewRenderer,
} from '@tiptap/react'
import { compact, orderBy } from 'lodash-es'
import {
  type JSX,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { AllergyMention } from '#src/components/AllergyMention.tsx'
import { CompositionMention } from '#src/components/CompositionMention.tsx'
import type { ConditionPickerCondition } from '#src/components/condition-picker/types.ts'
import { ConditionMention } from '#src/components/ConditionMention.tsx'
import { ConditionsCommand } from '#src/components/ConditionsCommand.tsx'
import { ComboboxCommand } from '#src/components/core/combobox/Combobox.tsx'
import { ComboboxAsyncCommand } from '#src/components/core/combobox/ComboboxAsync.tsx'
import { HoverParticipant } from '#src/components/core/HoverParticipant.tsx'
import { Link } from '#src/components/core/Link.tsx'
import { Popover } from '#src/components/core/popover/Popover.tsx'
import { PopoverAnchor } from '#src/components/core/popover/PopoverAnchor.tsx'
import { PopoverContent } from '#src/components/core/popover/PopoverContent.tsx'
import { Sheet } from '#src/components/core/sheet/Sheet.tsx'
import { SheetContent } from '#src/components/core/sheet/SheetContent.tsx'
import { DocumentPickerDialog } from '#src/components/document-picker/DocumentPickerDialog.tsx'
import type { DocumentPickerDocument } from '#src/components/document-picker/DocumentPickerView.tsx'
import { DocumentReferenceMention } from '#src/components/DocumentReferenceMention.tsx'
import { HoverComment } from '#src/components/HoverComment.tsx'
import { IconNewTask } from '#src/components/icons/IconNewTask.tsx'
import { ImmunizationMention } from '#src/components/ImmunizationMention.tsx'
import { InterventionMention } from '#src/components/InterventionMention.tsx'
import { MedicationMention } from '#src/components/MedicationMention.tsx'
import { ObservationMention } from '#src/components/ObservationMention.tsx'
import { AllergyOption } from '#src/components/options/AllergyOption.tsx'
import { ConditionOption } from '#src/components/options/ConditionOption.tsx'
import { DocumentReferenceOption } from '#src/components/options/DocumentReferenceOption.tsx'
import { InterventionOption } from '#src/components/options/InterventionOption.tsx'
import { ObservationOption } from '#src/components/options/ObservationOption.tsx'
import { ParticipantOption } from '#src/components/options/ParticipantOption.tsx'
import { PractitionerOption } from '#src/components/options/PractitionerOption.tsx'
import { ValidatedToolResponseOption } from '#src/components/options/ValidatedToolResponseOption.tsx'
import { OrderForm } from '#src/components/order/order-form/OrderForm.tsx'
import { OrderMention } from '#src/components/OrderMention.tsx'
import { OrderOption } from '#src/components/OrderOption.tsx'
import { buildConditionNoteHtml } from '#src/components/rich-text-editor/conditionNoteHtml.ts'
import { islandPortalAttributes } from '#src/components/rich-text-editor/islandPortalFocus.ts'
import { getConditionMentionSnapshot } from '#src/components/rich-text-editor/mention/conditionMention.ts'
import { TaskMention } from '#src/components/TaskMention.tsx'
import { ValidatedToolResponseMention } from '#src/components/ValidatedToolResponseMention.tsx'
import { valueSetAutocompleteLoadOptions } from '#src/components/ValueSetAutocomplete/valueSetAutocompleteLoadOptions.ts'
import { CompositionContext } from '#src/contexts/CompositionContext.tsx'
import { CurrentParticipantContext } from '#src/contexts/index.ts'
import { useIsRenderSafe } from '#src/contexts/RenderSafeContext.tsx'
import { useEncounterDiagnoses } from '#src/hooks/useEncounterDiagnoses.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { useLabCopySecondaryFilter } from '#src/hooks/useLabCopySecondaryFilter.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import { useOrderedConditionsList } from '#src/hooks/useOrderedConditions.ts'
import { useOrderSearchByIdentifier } from '#src/hooks/useOrderSearchByIdentifier.ts'
import { useParticipants } from '#src/hooks/useParticipants.ts'
import { usePortalStore } from '#src/hooks/usePortalStore.ts'
import { usePractitionersInternal } from '#src/hooks/usePractitionersInternal.ts'
import { useResource } from '#src/hooks/useResource.ts'
import { useSearch } from '#src/hooks/useSearch.ts'
import { useTaskComposerStore } from '#src/hooks/useTaskComposerStore.ts'
import { interventionLabel } from '#src/utils/interventionLabel.ts'

interface MentionNodeOptions {
  enablePractitionerMention?: boolean
  enableOrderTemplateMention?: boolean
  isTemplate?: boolean
  /* Restricts the @-picker to condition mentions only — every other option
   * (orders, docs, tasks, allergies, meds, labs, participants, templates, and
   * the non-mention insert buttons) is withheld. Set by the edit-before-accept
   * island (design §2.a), whose schema admits condition mentions + plain content
   * only: a non-condition option would either insert a never-insertable mention
   * subtype (bypassing the paste/seed filters via insertContentAt) or a node
   * type the island schema lacks (broken insert). Undefined everywhere else. */
  conditionsOnly?: boolean
  /* Called when a note template is inserted via @-mention so the host page can
   * union the template's labels + categories into the note. The mention node
   * holds only the editor, not the Composition; the page owns the single
   * label/category write path. Undefined outside a host note (e.g. the template
   * editor) → no-op merge. */
  onMergeTemplateMetadata?: (template: NoteTemplate) => void
}

export const MentionNode = MentionSchema.extend<MentionNodeOptions>({
  addKeyboardShortcuts() {
    return {
      '@': () => {
        const openId = generateUUID()
        window.__MENTION_NODE_OPEN_INITIAL_STATE_BY_ID__[openId] = true
        return this.editor
          .chain()
          .insertContentAt(this.editor.state.selection.head, {
            type: this.type.name,
            attrs: {
              openId,
            },
          })
          .focus()
          .run()
      },
    }
  },

  addNodeView() {
    return ReactNodeViewRenderer(MentionComponent, { className: 'group' })
  },

  addPasteRules() {
    return [
      nodePasteRule({
        find: TASK_REGEX,
        type: this.type,
        getAttributes: (match) => ({
          reference: `Task/${match[1]}`,
        }),
      }),
      nodePasteRule({
        find: COMMENT_REGEX,
        type: this.type,
        getAttributes: (match) => ({
          reference: `Communication/${match[1]}`,
        }),
      }),
      nodePasteRule({
        find: ORDER_REGEX,
        type: this.type,
        getAttributes: (match) => ({
          reference: `ServiceRequest/${match[1]}`,
        }),
      }),
      nodePasteRule({
        find: DOCUMENT_REFERENCE_PATH_REGEX,
        type: this.type,
        getAttributes: (match) => ({
          reference: `DocumentReference/${match[1]}`,
        }),
      }),
      nodePasteRule({
        find: DOCUMENT_REFERENCE_QUERY_REGEX,
        type: this.type,
        getAttributes: (match) => ({
          reference: `DocumentReference/${match[1]}`,
        }),
      }),
      nodePasteRule({
        find: VALIDATED_TOOL_RESPONSE_REGEX,
        type: this.type,
        getAttributes: (match) => ({
          reference: `QuestionnaireResponse/${match[1]}`,
        }),
      }),
      nodePasteRule({
        find: INTERVENTION_REGEX,
        type: this.type,
        getAttributes: (match) => ({
          reference: `CarePlan/${match[1]}`,
        }),
      }),
    ]
  },
})

const COMMENT_REGEX =
  /\bhttps?:\/\/[\w.-]+(?::\d+)?\/[\w\-/]+#(?:item|comment)-([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})\b/g

const TASK_REGEX =
  /\bhttps?:\/\/[\w.-]+(?::\d+)?(?:\/[\w\-/]+)?\/tasks\/([0-9a-fA-F]{8}(?:-[0-9a-fA-F]{4}){3}-[0-9a-fA-F]{12})(?:\/[\w\-/]*)?(?:\?[^\s]*)?\b/g

const ORDER_REGEX =
  /\bhttps?:\/\/[\w.-]+(?::\d+)?(?:\/[\w\-/]+)?\/orders\/([0-9a-fA-F]{8}(?:-[0-9a-fA-F]{4}){3}-[0-9a-fA-F]{12})(?:\/[\w\-/]*)?(?:\?[^\s]*)?\b/g

const DOCUMENT_REFERENCE_PATH_REGEX =
  /\bhttps?:\/\/[\w.-]+(?::\d+)?(?:\/[\w\-/]+)?\/documents\/([0-9a-fA-F]{8}(?:-[0-9a-fA-F]{4}){3}-[0-9a-fA-F]{12})(?:\/[\w\-/]*)?(?:\?[^\s]*)?\b/g

const DOCUMENT_REFERENCE_QUERY_REGEX =
  /\bhttps?:\/\/[\w.-]+(?::\d+)?(?:\/[\w\-/]+)?\/documents\?[^\s]*documentReferenceId=([0-9a-fA-F]{8}(?:-[0-9a-fA-F]{4}){3}-[0-9a-fA-F]{12})[^\s]*\b/g

const VALIDATED_TOOL_RESPONSE_REGEX =
  /\bhttps?:\/\/[\w.-]+(?::\d+)?(?:\/[\w\-/]+)?\/validated-tools\/responses\/([0-9a-fA-F]{8}(?:-[0-9a-fA-F]{4}){3}-[0-9a-fA-F]{12})(?:\/[\w\-/]*)?(?:\?[^\s]*)?\b/g

/* An intervention is a CarePlan nested under the goal care plan it belongs to,
 * so the captured id is the second uuid — the first names the parent plan. */
const INTERVENTION_REGEX =
  /\bhttps?:\/\/[\w.-]+(?::\d+)?(?:\/[\w\-/]+)?\/care-plans\/[0-9a-fA-F]{8}(?:-[0-9a-fA-F]{4}){3}-[0-9a-fA-F]{12}\/interventions\/([0-9a-fA-F]{8}(?:-[0-9a-fA-F]{4}){3}-[0-9a-fA-F]{12})(?:\/[\w\-/]*)?(?:\?[^\s]*)?\b/g

const CONDITION_WITH_NOTE_PREFIX = 'condition-with-note/'

/* The picker option set (design §2.a). Normally the full mention menu; in the
 * island, condition mentions ONLY. Two things happen in the island branch: the
 * non-condition options are dropped (conditionOptions holds conditions only, so
 * selecting conditionOptions instead of the full list withholds every order /
 * doc / task / allergy / med / lab / participant / template / insert-button
 * option), AND the "+ most recent note" variant is filtered out — it injects the
 * prior note's body as raw HTML via insertContentAt, which bypasses the island's
 * mention-subtype filter, so a non-condition mention embedded in that note body
 * would otherwise enter unfiltered. The plain condition mention stays insertable;
 * pulling a whole prior note into a single-suggestion edit region is marginal.
 * Exported so the island scoping is unit-tested directly, not just via the flag. */
export function scopePickerOptions<T extends { value: string }>(
  conditionsOnly: boolean | undefined,
  fullOptions: T[],
  conditionOptions: T[]
): T[] {
  if (!conditionsOnly) {
    return fullOptions
  }
  return conditionOptions.filter(
    (option) => !option.value.startsWith(CONDITION_WITH_NOTE_PREFIX)
  )
}

/* Anchor a condition's two mention rows ("<Condition>" and "<Condition> +
   most recent note") to the same group so the combobox keeps them adjacent
   at the higher-scoring variant's rank. */
function getConditionPairGroupKey(option: {
  value: string
}): string | undefined {
  if (option.value.startsWith(CONDITION_WITH_NOTE_PREFIX)) {
    return option.value.slice(CONDITION_WITH_NOTE_PREFIX.length)
  }
  if (option.value.startsWith('Condition/')) {
    return option.value
  }
  return undefined
}

function findEnclosingBulletListItemDepth($pos: ResolvedPos): number | null {
  for (let d = $pos.depth; d > 0; d--) {
    if ($pos.node(d).type.name !== 'listItem') {
      continue
    }
    for (let d2 = d - 1; d2 > 0; d2--) {
      if ($pos.node(d2).type.name === 'bulletList') {
        return d
      }
    }
    return null
  }
  return null
}

interface VitalOption {
  coding: CodeableConcept
  display: string
  abbreviation?: string
  unit: string | null
  scaleType: ScaleType | null
  answerOptions?: { code: string; display: string }[]
}

const VITAL_OPTIONS: VitalOption[] = [
  {
    coding: BLOOD_PRESSURE_CODING,
    display: 'Blood Pressure',
    abbreviation: 'BP',
    unit: BLOOD_PRESSURE_UNIT,
    scaleType: BLOOD_PRESSURE_SCALE_TYPE,
  },
  {
    coding: HEART_RATE_CODING,
    display: 'Heart Rate',
    abbreviation: 'HR',
    unit: HEART_RATE_UNIT,
    scaleType: HEART_RATE_SCALE_TYPE,
  },
  {
    coding: RESPIRATORY_RATE_CODING,
    display: 'Respiration Rate',
    abbreviation: 'RR',
    unit: RESPIRATORY_RATE_UNIT,
    scaleType: RESPIRATORY_RATE_SCALE_TYPE,
  },
  {
    coding: BODY_TEMPERATURE_CODING,
    display: 'Temperature',
    abbreviation: 'Temp',
    unit: BODY_TEMPERATURE_UNIT,
    scaleType: BODY_TEMPERATURE_SCALE_TYPE,
  },
  {
    coding: OXYGEN_SATURATION_CODING,
    display: 'Oxygen Saturation',
    abbreviation: 'O2',
    unit: OXYGEN_SATURATION_UNIT,
    scaleType: OXYGEN_SATURATION_SCALE_TYPE,
  },
  {
    coding: BODY_HEIGHT_CODING,
    display: 'Height',
    abbreviation: 'Ht',
    unit: BODY_HEIGHT_UNIT,
    scaleType: BODY_HEIGHT_SCALE_TYPE,
  },
  {
    coding: BODY_WEIGHT_CODING,
    display: 'Weight',
    abbreviation: 'Wt',
    unit: BODY_WEIGHT_UNIT,
    scaleType: BODY_WEIGHT_SCALE_TYPE,
  },
  {
    coding: BODY_MASS_INDEX_CODING,
    display: 'BMI',
    unit: BODY_MASS_INDEX_UNIT,
    scaleType: BODY_MASS_INDEX_SCALE_TYPE,
  },
  {
    coding: PAIN_SEVERITY_CODING,
    display: 'Pain Severity',
    abbreviation: 'Pain',
    unit: PAIN_SEVERITY_UNIT,
    scaleType: PAIN_SEVERITY_SCALE_TYPE,
    answerOptions: PAIN_SEVERITY_ANSWER_OPTIONS,
  },
]

function MentionComponent(props: NodeViewProps): JSX.Element {
  const referenceString = props.node.attrs.reference as string | null
  const reference = parseReferenceString(referenceString)
  /* The label an AI author wrote, or null on a chip a clinician placed — the
   * suggestion pipeline sets it, the @-picker never does. */
  const authoredLabel = props.node.attrs.authoredLabel as string | null
  const derivedLabel = props.node.attrs.derivedLabel as string | null
  /* An authored label only exists because a skill wrote a citation, so reading
   * one belongs to the citation feature and waits on the same flag. Off the
   * flag every citation chip re-derives its text from the live resource,
   * exactly as it does today. */
  const citationMentionsEnabled = useFeatureFlag(
    featureFlags.aiCitationMentions
  )

  if (isOfType(reference, 'Task')) {
    return <TaskMentionComponent task={reference} />
  }

  if (isOfType(reference, 'Communication')) {
    return <CommentMentionComponent comment={reference} />
  }

  /* A derived label exists only on a citation chip, naming the part of the
   * chart the cited line came from, so it wins over the record's own name here
   * and on the condition, observation and medication chips below. */
  if (isOfType(reference, 'ServiceRequest')) {
    const displayText = props.node.attrs.display as string | null
    return (
      <OrderMentionComponent
        order={reference}
        display={derivedLabel ?? displayText}
      />
    )
  }

  /* A citation chip cites demographics through the participant record; the
   * @-picker's participant mention has no derived label and keeps its hover
   * card. */
  if (isOfType(reference, 'Patient') && derivedLabel) {
    return (
      <DemographicsCitationMentionComponent
        participant={reference}
        display={derivedLabel}
      />
    )
  }

  if (isOfType(reference, 'Patient')) {
    return <ParticipantMentionComponent participant={reference} />
  }

  if (isOfType(reference, 'Condition')) {
    const displayText = props.node.attrs.display as string | null
    return (
      <ConditionMentionComponent
        condition={reference}
        display={derivedLabel ?? displayText}
      />
    )
  }

  if (isOfType(reference, 'AllergyIntolerance')) {
    return <AllergyMentionComponent allergy={reference} />
  }

  if (isOfType(reference, 'DocumentReference')) {
    const displayText = props.node.attrs.display as string | null
    return (
      <DocumentReferenceMentionComponent
        documentReference={reference}
        display={derivedLabel ?? displayText}
      />
    )
  }

  if (isOfType(reference, 'Composition')) {
    return (
      <CompositionMentionComponent
        composition={reference}
        display={
          derivedLabel ?? (citationMentionsEnabled ? authoredLabel : null)
        }
      />
    )
  }

  if (isOfType(reference, 'QuestionnaireResponse')) {
    return (
      <ValidatedToolResponseMentionComponent
        validatedToolResponse={reference}
        display={
          derivedLabel ?? (citationMentionsEnabled ? authoredLabel : null)
        }
      />
    )
  }

  if (isOfType(reference, 'Observation')) {
    return (
      <ObservationMentionComponent
        observation={reference}
        display={derivedLabel}
      />
    )
  }

  if (isOfType(reference, 'MedicationRequest')) {
    const displayText = props.node.attrs.display as string | null
    return (
      <MedicationMentionComponent
        medication={reference}
        display={derivedLabel ?? displayText}
      />
    )
  }

  if (isOfType(reference, 'Immunization')) {
    return (
      <ImmunizationMentionComponent
        immunization={reference}
        display={derivedLabel}
      />
    )
  }

  /* Only a citation chip. The @-picker offers no care team, so a care-team
   * mention without a derived label keeps falling through as it always has. */
  if (isOfType(reference, 'CareTeam') && derivedLabel) {
    return <CareTeamMentionComponent display={derivedLabel} />
  }

  /* No `display` passed, as the allergy, composition and questionnaire-response
   * chips do: the label is re-derived from the live intervention on every
   * render. `display` exists to give the stored note body searchable text, not
   * to be read back — it round-trips as the anchor's own text and so carries
   * the `@` the picker writes in front of every mention. Reading it is what put
   * an `@` on screen.
   *
   * The resource type comes from the model rather than a literal, so this
   * branch cannot drift from what `Intervention` actually is. It is only the
   * coarse test — a goal or initial care plan is also a `CarePlan` — and the
   * chip asks `Intervention.isInstance` once the resource has resolved. */
  if (isOfType(reference, Intervention.RESOURCE_TYPE)) {
    return <InterventionMentionComponent intervention={reference} />
  }

  return <FallbackMentionComponent {...props} />
}

function TaskMentionComponent({
  task,
}: {
  task: Reference<Task>
}): JSX.Element {
  return (
    <NodeViewWrapper as="span" className="inline">
      <TaskMention task={task} />
    </NodeViewWrapper>
  )
}

function OrderMentionComponent({
  order,
  display,
}: {
  order: Reference<ServiceRequest>
  display?: string | null
}): JSX.Element {
  return (
    <NodeViewWrapper as="span" className="inline">
      <OrderMention order={order} displayOverride={display} />
    </NodeViewWrapper>
  )
}

function CommentMentionComponent({
  comment: commentReference,
}: {
  comment: Reference<Communication>
}): JSX.Element {
  return (
    <NodeViewWrapper as="span" className="inline">
      {commentReference.reference && (
        <HoverComment comment={commentReference.reference} />
      )}
    </NodeViewWrapper>
  )
}

function ParticipantMentionComponent({
  participant,
}: {
  participant: Reference<Patient>
}): JSX.Element {
  return (
    <NodeViewWrapper as="span" className="inline">
      <HoverParticipant participant={participant} prefix="@" />
    </NodeViewWrapper>
  )
}

function DemographicsCitationMentionComponent({
  participant,
  display,
}: {
  participant: Reference<Patient>
  display: string
}): JSX.Element {
  const participantId = resolveId(participant)
  const url = participantId ? `/Patient/${participantId}` : '#'
  return (
    <NodeViewWrapper as="span" className="inline">
      <Link
        to={url}
        className="rounded-sm border-[0.5px] border-gray-450 bg-gray-470 box-decoration-clone px-1 py-px select-none group-[.ProseMirror-selectednode]:bg-[lch(53_52.26_286.91/0.4)] hover:bg-gray-460 [&&]:no-underline"
        tabIndex={-1}
        aria-disabled={!participantId}
      >
        <span>{display}</span>
      </Link>
    </NodeViewWrapper>
  )
}

function ImmunizationMentionComponent({
  immunization,
  display,
}: {
  immunization: Reference<Immunization>
  display?: string | null
}): JSX.Element {
  return (
    <NodeViewWrapper as="span" className="inline">
      <ImmunizationMention
        immunization={immunization}
        displayOverride={display}
      />
    </NodeViewWrapper>
  )
}

function ConditionMentionComponent({
  condition,
  display,
}: {
  condition: Reference<Condition>
  display?: string | null
}): JSX.Element {
  return (
    <NodeViewWrapper as="span" className="inline">
      <ConditionMention condition={condition} displayOverride={display} />
    </NodeViewWrapper>
  )
}

function AllergyMentionComponent({
  allergy,
}: {
  allergy: Reference<AllergyIntolerance>
}): JSX.Element {
  return (
    <NodeViewWrapper as="span" className="inline">
      <AllergyMention allergy={allergy} />
    </NodeViewWrapper>
  )
}

function DocumentReferenceMentionComponent({
  documentReference,
  display,
}: {
  documentReference: Reference<DocumentReference>
  display?: string | null
}): JSX.Element {
  return (
    <NodeViewWrapper as="span" className="inline-flex">
      <DocumentReferenceMention
        documentReference={documentReference}
        displayOverride={display}
      />
    </NodeViewWrapper>
  )
}

function InterventionMentionComponent({
  intervention,
}: {
  intervention: Reference<CarePlan>
}): JSX.Element {
  return (
    <NodeViewWrapper as="span" className="inline-flex">
      <InterventionMention intervention={intervention} />
    </NodeViewWrapper>
  )
}

function CompositionMentionComponent({
  composition,
  display,
}: {
  composition: Reference<Composition>
  display?: string | null
}): JSX.Element {
  return (
    <NodeViewWrapper as="span" className="inline">
      <CompositionMention composition={composition} displayOverride={display} />
    </NodeViewWrapper>
  )
}

function ValidatedToolResponseMentionComponent({
  validatedToolResponse,
  display,
}: {
  validatedToolResponse: Reference<QuestionnaireResponse>
  display?: string | null
}): JSX.Element {
  return (
    <NodeViewWrapper as="span" className="inline">
      <ValidatedToolResponseMention
        validatedToolResponse={validatedToolResponse}
        displayOverride={display}
      />
    </NodeViewWrapper>
  )
}

function ObservationMentionComponent({
  observation,
  display,
}: {
  observation: Reference<Observation>
  display?: string | null
}): JSX.Element {
  return (
    <NodeViewWrapper as="span" className="inline">
      <ObservationMention observation={observation} displayOverride={display} />
    </NodeViewWrapper>
  )
}

/* Not a link: the care team lives on a facesheet tab no URL selects, and the
 * org-wide care-teams page is not about this participant. */
function CareTeamMentionComponent({
  display,
}: {
  display: string
}): JSX.Element {
  return (
    <NodeViewWrapper as="span" className="inline">
      <span className="rounded-sm border-[0.5px] border-gray-450 bg-gray-470 box-decoration-clone px-1 py-px select-none group-[.ProseMirror-selectednode]:bg-[lch(53_52.26_286.91/0.4)]">
        <span>
          <IconUsersGroup className="inline-block size-[1em] align-[-0.25ex]" />
        </span>
        <span> {display}</span>
      </span>
    </NodeViewWrapper>
  )
}

function MedicationMentionComponent({
  medication,
  display,
}: {
  medication: Reference<MedicationRequest>
  display?: string | null
}): JSX.Element {
  return (
    <NodeViewWrapper as="span" className="inline">
      <MedicationMention medication={medication} displayOverride={display} />
    </NodeViewWrapper>
  )
}

function FallbackMentionComponent(props: NodeViewProps): JSX.Element {
  // We need a way to open the popover when the node is created, but we also need to make sure the popover is only opened on the client that typed the keyboard shortcut above. window seemed like the best way to pass information between the addKeyboardShortcuts method and the component.
  const openId = props.node.attrs.openId as string | null
  const [open, setOpen] = useState(
    openId
      ? (window.__MENTION_NODE_OPEN_INITIAL_STATE_BY_ID__[openId] ?? false)
      : false
  )
  const [showOrderSheet, setShowOrderSheet] = useState(false)
  const isOpeningOrderSheetRef = useRef(false)
  const isSubmittingOrderRef = useRef(false)
  const [showDocumentPicker, setShowDocumentPicker] = useState(false)
  const isOpeningDocumentPickerRef = useRef(false)
  const isInsertingDocumentsRef = useRef(false)
  const [inlineConditionSearch, setInlineConditionSearch] = useState<
    string | null
  >(null)
  const isSwitchingToInlineConditionRef = useRef(false)
  const isConfirmingInlineConditionRef = useRef(false)

  const updateAttributes = props.updateAttributes
  useEffect(() => {
    if (openId) {
      delete window.__MENTION_NODE_OPEN_INITIAL_STATE_BY_ID__[openId]
      setTimeout(() => {
        // updateAttributes within useEffect needs setTimeout to avoid triggering a warning: https://github.com/ueberdosis/tiptap/issues/3764#issuecomment-1546854730
        updateAttributes({
          openId: null,
        })
      })
    }
  }, [openId, updateAttributes])

  const referenceString = props.node.attrs.reference as string | null
  const reference = parseReferenceString(referenceString)
  const { data: resource } = useResource(reference)
  const nodeDisplay = props.node.attrs.display as string | null
  const displayString = resource
    ? referenceDisplay(createReference(resource))
    : nodeDisplay?.replace('@', '')

  const extension = props.extension as typeof MentionNode

  const isTemplate = extension.options.isTemplate
  const conditionsOnly = extension.options.conditionsOnly

  const internalPractitioners = usePractitionersInternal()
  const practitioners = extension.options.enablePractitionerMention
    ? internalPractitioners
    : null

  const currentParticipant = useContext(CurrentParticipantContext)

  // In a participant-scoped note, load the participant's full order list so
  // Fuse can fuzzy-match on title/description/etc. via `order.searchKeywords`
  // (see `orderOptions`). In global contexts (e.g. desk pages) there's no
  // participant, so fall back to on-demand SR-identifier lookup rather than an
  // unbounded org-wide fetch.
  const [orderSearch, setOrderSearch] = useState('')
  const { data: identifierOrders } = useOrderSearchByIdentifier(
    currentParticipant ? '' : orderSearch
  )
  const { data: participantOrders } = useSearch(
    Order,
    currentParticipant
      ? {
          ...Order.byParticipant({ participant: currentParticipant.reference }),
          // Revoked/entered-in-error orders are dead and shouldn't be mentionable.
          'status:not': 'revoked,entered-in-error,draft',
        }
      : undefined
  )
  const orders = currentParticipant ? participantOrders : identifierOrders

  const { data: allergies } = useSearch(
    ExtendedAllergy,
    currentParticipant
      ? ExtendedAllergy.searchQuery({
          participant: currentParticipant.reference,
        })
      : undefined
  )

  const { data: medications } = useSearch(
    ExtendedMedicationRequest,
    currentParticipant
      ? ExtendedMedicationRequest.queryAllBySubject(
          currentParticipant.reference.reference,
          { includeDraft: false }
        )
      : undefined
  )

  const withoutLabCopySecondaries = useLabCopySecondaryFilter()
  const { data: documentReferences } = useSearch(
    ExtendedDocumentReference,
    currentParticipant
      ? withoutLabCopySecondaries(
          ExtendedDocumentReference.byParticipant({
            participant: currentParticipant.reference,
          })
        )
      : undefined
  )

  const { data: validatedToolResponses } = useSearch(
    ValidatedToolResponse,
    currentParticipant
      ? ValidatedToolResponse.byParticipant({
          participant: currentParticipant.reference,
        })
      : undefined
  )

  const { data: labObservations } = useSearch(
    ExtendedObservation,
    currentParticipant
      ? ExtendedObservation.labSearchQuery({
          subject: currentParticipant.reference,
          codes: LAB_OPTIONS.map((l) => l.coding),
        })
      : undefined
  )

  const medicationRefillUiEnabled = useFeatureFlag(
    featureFlags.medicationRefillTaskingUi
  )

  const { data: interventions } = useSearch(
    Intervention,
    currentParticipant
      ? Intervention.queryMentionableByParticipant(currentParticipant.reference)
      : undefined
  )

  const { orderedConditions } = useOrderedConditionsList(
    currentParticipant?.reference
  )

  const { data: conditionComments } = useSearch(
    CommentCommunication,
    orderedConditions.length > 0
      ? CommentCommunication.searchQuery(
          orderedConditions.map((c) => c.reference)
        )
      : undefined
  )

  /* conditionComments is sorted by -sent, so the first match per condition wins. */
  const mostRecentNoteByConditionRef = useMemo(() => {
    const map = new Map<string, CommentCommunication>()
    for (const comment of conditionComments ?? []) {
      for (const part of comment.partOf ?? []) {
        if (
          isOfType(part, 'Condition') &&
          part.reference &&
          !map.has(part.reference)
        ) {
          map.set(part.reference, comment)
        }
      }
    }
    return map
  }, [conditionComments])

  const { data: orderTemplates } = useSearch(
    OrderTemplate,
    extension.options.enableOrderTemplateMention
      ? OrderTemplate.searchQuery()
      : undefined
  )

  const { data: noteTemplates } = useSearch(
    NoteTemplate,
    NoteTemplate.searchQuery()
  )

  const noteTemplateOptions = useMemo(
    () =>
      (noteTemplates ?? []).map((noteTemplate) => ({
        value: noteTemplate.reference.reference,
        label: (
          <div className="flex items-center gap-2">
            <div className="p-0.5">
              <IconTemplate className="size-4 shrink-0" />
            </div>
            {noteTemplate.title}
          </div>
        ),
        keywords: [noteTemplate.title],
      })),
    [noteTemplates]
  )

  const participants = useParticipants()

  const existsCurrentParticipant = !!currentParticipant

  const participantOptions = useMemo(
    () =>
      participants
        .filter((participant) => {
          if (!currentParticipant) {
            return true
          }
          return (
            participant.reference.reference ===
            currentParticipant.reference.reference
          )
        })
        .map((participant) => ({
          value: participant.reference.reference,
          label: <ParticipantOption participant={participant.reference} />,
          keywords: participant.searchKeywords,
        })),
    [participants, currentParticipant]
  )

  const practitionerOptions = useMemo(
    () =>
      (practitioners ?? []).map((practitioner) => ({
        value: practitioner.reference.reference,
        label: <PractitionerOption practitioner={practitioner.reference} />,
        keywords: practitioner.searchKeywords,
      })),
    [practitioners]
  )

  const orderOptions = useMemo(
    () =>
      (orders ?? []).map((order) => ({
        value: order.reference.reference,
        label: <OrderOption order={order.reference} />,
        // searchKeywords covers seenIdentifier variants plus title/description/
        // reason/specialty so Fuse matches both identifier and free-text input.
        keywords: order.searchKeywords,
      })),
    [orders]
  )

  const conditionOptions = useMemo(
    () =>
      orderedConditions.flatMap((condition) => {
        const refString = getReferenceString(condition.reference)
        return [
          {
            value: refString,
            label: <ConditionOption condition={condition.reference} />,
            keywords: condition.searchKeywords,
          },
          {
            value: `${CONDITION_WITH_NOTE_PREFIX}${refString}`,
            label: (
              <ConditionOption
                condition={condition.reference}
                suffix="+ most recent note"
              />
            ),
            /* Use the full displayed label as the only +note keyword so the
               variant only ranks high when the user's query actually includes
               the condition title — typing "note" alone shouldn't surface
               every +note row indiscriminately. */
            keywords: [
              ...condition.searchKeywords,
              `${condition.title} + most recent note`,
            ],
          },
        ]
      }),
    [orderedConditions]
  )

  const medicationOptions = useMemo(
    () =>
      (medications ?? [])
        .filter((med) => med.status === 'active' && med.medicationDisplay)
        .map((med) => ({
          value: getReferenceString(med.reference),
          label: (
            <div className="flex items-center gap-2">
              <div className="p-0.5">
                <IconPill className="size-4 shrink-0" />
              </div>
              {med.medicationDisplay}
            </div>
          ),
          keywords: [med.medicationDisplay ?? ''],
        })),
    [medications]
  )

  const allergyOptions = useMemo(
    () =>
      (allergies ?? []).map((allergy) => ({
        value: getReferenceString(allergy.reference),
        label: <AllergyOption allergy={allergy.reference} />,
        keywords: allergy.searchKeywords,
      })),
    [allergies]
  )

  const documentReferenceOptions = useMemo(
    () =>
      (documentReferences ?? []).map((docRef) => ({
        value: getReferenceString(docRef.reference),
        label: <DocumentReferenceOption documentReference={docRef.reference} />,
        keywords: [docRef.title], // Keywords should match what's shown in the label
      })),
    [documentReferences]
  )

  /* Active first, then by title. An inactive intervention stays selectable — a
   * note explaining why one was discontinued has to be able to name it — but it
   * should never outrank the care currently being delivered. */
  /* Labelled once, then sorted, keyworded and rendered off that one string.
   * `interventionLabel` parses the intervention's stored description, so
   * calling it from the sort comparator, the keywords and the row would run it
   * three times per intervention on a picker that remounts on every `@`. */
  const interventionOptions = useMemo(
    () =>
      /* Newest first, on the created date the row shows — the same direction
       * every other participant-scoped loader here sorts (`_sort:
       * -_lastUpdated`), so the picker does not reverse itself between
       * sections. */
      orderBy(
        (interventions ?? []).map((intervention) => ({
          intervention,
          label: interventionLabel(intervention),
        })),
        [({ intervention }) => intervention.created ?? ''],
        ['desc']
      ).map(({ intervention, label }) => ({
        value: getReferenceString(intervention.reference),
        label: <InterventionOption intervention={intervention} label={label} />,
        /* Keywords should match what's shown in the label, plus the type word
         * so typing "intervention" surfaces them — as the validated-tool and
         * lab rows carry 'validated tool' and 'lab'. It sits last and is the
         * same string on every row, so Fuse scores each option on its
         * best-matching keyword: a row whose own text matches the query still
         * outranks rows matching only the type word. */
        keywords: [label, ...intervention.idtRoles, 'intervention'],
      })),
    [interventions]
  )

  const validatedToolResponseOptions = useMemo(() => {
    const latestByName = new Map<string, ValidatedToolResponse>()
    for (const response of validatedToolResponses ?? []) {
      if (!response.name) {
        continue
      }
      const existing = latestByName.get(response.name)
      if (
        !existing ||
        new Date(response.authored).getTime() >
          new Date(existing.authored).getTime()
      ) {
        latestByName.set(response.name, response)
      }
    }
    return [...latestByName.values()].map((response) => ({
      value: getReferenceString(response.reference),
      label: (
        <ValidatedToolResponseOption
          validatedToolResponse={response.reference}
        />
      ),
      keywords: [response.name ?? '', 'validated tool'],
    }))
  }, [validatedToolResponses])

  const labObservationOptions = useMemo(() => {
    const latestByCode = new Map<string, ExtendedObservation>()
    for (const obs of labObservations ?? []) {
      const code = codeFromCodeableConcept(obs.codeableConcept)
      if (!code) {
        continue
      }
      const existing = latestByCode.get(code)
      if (
        !existing ||
        new Date(obs.effectiveDateTime ?? '').getTime() >
          new Date(existing.effectiveDateTime ?? '').getTime()
      ) {
        latestByCode.set(code, obs)
      }
    }
    return [...latestByCode.values()].map((obs) => {
      const labOption = LAB_OPTIONS.find(
        (l) =>
          codeFromCodeableConcept(l.coding) ===
          codeFromCodeableConcept(obs.codeableConcept)
      )
      return {
        value: getReferenceString(obs.reference),
        label: <ObservationOption observation={obs.reference} />,
        keywords: [
          // lint-reference-display-ignore -- lab test name
          labOption?.display ?? '',
          ...(labOption?.abbreviation ? [labOption.abbreviation] : []),
          'lab',
        ],
      }
    })
  }, [labObservations])

  const pickerDocuments: DocumentPickerDocument[] = useMemo(
    () =>
      (documentReferences ?? [])
        .slice()
        .sort((a, b) => b.date.getTime() - a.date.getTime())
        .map((docRef) => ({
          id: docRef.id,
          title: docRef.title,
          date: docRef.dateStringWithFallback ?? '',
          categories: docRef.categories,
          attachments: docRef.attachments,
          isSigned: docRef.isConfirmed,
          subtitle: docRef.description || docRef.aiSummary || null,
          description: docRef.description || null,
          aiSummary: docRef.aiSummary || null,
          author: referenceDisplay(docRef.author),
          transcription: docRef.transcription ?? null,
        })),
    [documentReferences]
  )

  const orderTemplateOptions = useMemo(
    () =>
      (orderTemplates ?? []).map((orderTemplate) => ({
        value: orderTemplate.reference.reference,
        label: (
          <div className="flex items-center gap-2">
            <div className="p-0.5">
              <IconTemplate className="size-4 shrink-0" />
            </div>
            {orderTemplate.title}
          </div>
        ),
        keywords: [orderTemplate.title],
      })),
    [orderTemplates]
  )

  const compositionReferenceString = useContext(CompositionContext)

  const { addConditions } = useEncounterDiagnoses(compositionReferenceString)

  /* Inside an AI suggestion's widget a pick is a draft edit, not the clinical
   * act, so the chip carries the UNVERSIONED reference and accept does the
   * pinning. See ConditionMentionSnapshotOptions.pinVersion for why a pinned
   * chip would make the suggestion unacceptable. */
  const pinVersion = !useIsRenderSafe()

  const showAdminActions = usePortalStore((s) => s.showAdminActions)

  const options = useMemo(
    () =>
      compact([
        (compositionReferenceString || isTemplate) && {
          value: 'signature-button',
          label: (
            <div className="flex items-center gap-2">
              <div className="p-0.5">
                <IconSignature className="size-4 shrink-0" />
              </div>
              Signature field
            </div>
          ),
          keywords: ['Signature field'],
        },
        isTemplate && {
          value: 'choice-button',
          label: (
            <div className="flex items-center gap-2">
              <div className="p-0.5">
                <IconSquareRoundedCheck className="size-4 shrink-0" />
              </div>
              Template Button
            </div>
          ),
          keywords: ['template button', 'button', 'choice', 'options'],
        },
        ((compositionReferenceString && existsCurrentParticipant) ||
          isTemplate) && {
          value: 'appointment-node',
          label: (
            <div className="flex items-center gap-2">
              <div className="p-0.5">
                <IconCalendar className="size-4" />
              </div>
              Appointment
            </div>
          ),
          keywords: ['appointment'],
        },
        ...((compositionReferenceString && existsCurrentParticipant) ||
        isTemplate
          ? questionnaires.VALIDATED_TOOLS_ARRAY.map((tool) => ({
              value: `validated-tool/${tool.name.toLowerCase()}`,
              label: (
                <div className="flex items-center gap-2">
                  <div className="p-0.5">
                    <IconClipboardCheck className="size-4 shrink-0" />
                  </div>
                  {tool.name}
                </div>
              ),
              keywords: [tool.name],
            }))
          : []),
        showAdminActions && {
          value: 'observation-node',
          label: (
            <div className="flex items-center gap-2">
              <div className="p-0.5">
                <IconFlask className="size-4 shrink-0" />
              </div>
              Observation
            </div>
          ),
          keywords: ['observation', 'lab', 'test', 'result'],
        },
        ...VITAL_OPTIONS.map((vital) => ({
          value: `vital/${codeFromCodeableConcept(vital.coding)}`,
          label: (
            <div className="flex items-center gap-2">
              <div className="p-0.5">
                <IconActivity className="size-4 shrink-0" />
              </div>
              <span>
                {vital.display}
                {vital.abbreviation && (
                  <span className="text-muted-foreground">
                    {' '}
                    · {vital.abbreviation}
                  </span>
                )}
              </span>
            </div>
          ),
          keywords: [
            vital.display.toLowerCase(),
            'vital',
            ...(vital.abbreviation ? [vital.abbreviation.toLowerCase()] : []),
          ],
        })),
        (compositionReferenceString || isTemplate) && {
          value: 'add-order',
          label: (
            <div className="flex items-center gap-2">
              <div className="p-0.5">
                <IconPlus className="size-4 shrink-0" />
              </div>
              Add Order
            </div>
          ),
          keywords: ['add order', 'new order', 'create order'],
        },
        {
          value: 'create-task',
          label: (
            <div className="flex items-center gap-2">
              <div className="p-0.5">
                <IconNewTask className="size-4 shrink-0" />
              </div>
              Create Task
            </div>
          ),
          keywords: ['create task', 'new task', 'add task', 'task'],
        },
        existsCurrentParticipant && {
          value: 'insert-documents',
          label: (
            <div className="flex items-center gap-2">
              <div className="p-0.5">
                <IconFile className="size-4 shrink-0" />
              </div>
              Insert Documents
            </div>
          ),
          keywords: [
            'Insert Documents',
            'documents',
            'docs',
            'reports',
            'attach',
            'files',
          ],
        },
        {
          value: 'add-timeline-note-button',
          label: (
            <div className="flex items-center gap-2">
              <div className="p-0.5">
                <IconPlus className="size-4 shrink-0" />
              </div>
              &ldquo;New chart note…&rdquo; button
            </div>
          ),
          keywords: ['New chart note button'],
        },
        {
          value: 'insert-conditions-button',
          label: (
            <div className="flex items-center gap-2">
              <div className="p-0.5">
                <IconClipboardPlus className="size-4 shrink-0" />
              </div>
              Insert Past Medical History
            </div>
          ),
          keywords: ['Insert Past Medical History', 'Insert PMH'],
        },
        {
          value: 'insert-current-conditions-button',
          label: (
            <div className="flex items-center gap-2">
              <div className="p-0.5">
                <IconClipboardPlus className="size-4 shrink-0" />
              </div>
              Insert Current Conditions
            </div>
          ),
          keywords: ['Insert Current Conditions', 'Insert Current Diagnoses'],
        },
        {
          value: 'insert-current-conditions-with-notes-button',
          label: (
            <div className="flex items-center gap-2">
              <div className="p-0.5">
                <IconClipboardPlus className="size-4 shrink-0" />
              </div>
              Insert Current Conditions + most recent notes
            </div>
          ),
          keywords: [
            'Insert Current Conditions + most recent notes',
            'Insert Current Diagnoses + most recent notes',
          ],
        },
        {
          value: 'insert-medications-button',
          label: (
            <div className="flex items-center gap-2">
              <div className="p-0.5">
                <IconPill className="size-4 shrink-0" />
              </div>
              Insert Current Medications
            </div>
          ),
          keywords: ['Insert Current Medications', 'Insert Current Meds'],
        },
        {
          value: 'insert-allergies-button',
          label: (
            <div className="flex items-center gap-2">
              <div className="p-0.5">
                <IconVirus className="size-4 shrink-0" />
              </div>
              Insert Current Allergies
            </div>
          ),
          keywords: ['Insert Current Allergies'],
        },
        (compositionReferenceString || isTemplate) && {
          value: 'encounter-medications-block',
          label: (
            <div className="flex items-center gap-2">
              <div className="p-0.5">
                <IconPill className="size-4 shrink-0" />
              </div>
              Medications Ordered This Visit
            </div>
          ),
          keywords: ['Medications Ordered This Visit', 'Medication Orders'],
        },
        {
          value: 'conditions-addressed-block',
          label: (
            <div className="flex items-center gap-2">
              <div className="p-0.5">
                <IconListCheck className="size-4 shrink-0" />
              </div>
              Diagnoses Addressed This Visit
            </div>
          ),
          keywords: [
            'Diagnoses Addressed This Visit',
            'Conditions Addressed This Visit',
            'Problems Addressed This Visit',
          ],
        },
        medicationRefillUiEnabled &&
          (compositionReferenceString || isTemplate) && {
            value: 'medication-refill-block',
            label: (
              <div className="flex items-center gap-2">
                <div className="p-0.5">
                  <IconPill className="size-4 shrink-0" />
                </div>
                Medications Needing Refills
              </div>
            ),
            keywords: [
              'Medications Needing Refills',
              'Ask About Medication Refills',
              'Medication Refill',
              'Cycle Meds',
              'PCP',
            ],
          },
        ...noteTemplateOptions,
        ...orderTemplateOptions,
        ...practitionerOptions,
        ...participantOptions,
        ...orderOptions,
        ...conditionOptions,
        ...allergyOptions,
        ...documentReferenceOptions,
        ...medicationOptions,
        ...validatedToolResponseOptions,
        ...labObservationOptions,
        ...interventionOptions,
      ]),
    [
      practitionerOptions,
      noteTemplateOptions,
      orderTemplateOptions,
      participantOptions,
      compositionReferenceString,
      isTemplate,
      orderOptions,
      conditionOptions,
      allergyOptions,
      documentReferenceOptions,
      medicationOptions,
      validatedToolResponseOptions,
      labObservationOptions,
      interventionOptions,
      showAdminActions,
      existsCurrentParticipant,
      medicationRefillUiEnabled,
    ]
  )

  // Island (design §2.a): condition mentions only — see scopePickerOptions.
  const pickerOptions = scopePickerOptions(
    conditionsOnly,
    options,
    conditionOptions
  )

  const medplum = useMedplum()

  const handleObservationSelect = async (
    result: Coding | null
  ): Promise<void> => {
    if (!result) {
      return
    }
    const { code, system } = result
    const { scaleType, unit, answerOptions, display } = await getCodingDetails(
      medplum,
      result
    )
    const pos = props.getPos()
    if (pos === undefined) {
      return
    }
    props.deleteNode()
    props.editor
      .chain()
      .insertContentAt(pos, {
        type: 'observation',
        attrs: {
          display,
          codingDisplay: display,
          system,
          code,
          scaleType,
          unit,
          answerOptions,
        },
      })
      .focus()
      .run()
  }

  const loadObservationOptions = useCallback(
    async ({ input, signal }: { input: string; signal: AbortSignal }) =>
      valueSetAutocompleteLoadOptions({
        input,
        signal,
        medplum,
        binding: codeSystems.LOINC_OBSERVATIONS_VALUE_SET,
      }),
    [medplum]
  )

  const [showObservationAutocomplete, setShowObservationAutocomplete] =
    useState(false)

  const handleOrderSubmit = useCallback(
    (order: Order): void => {
      isSubmittingOrderRef.current = true
      setShowOrderSheet(false)
      const pos = props.getPos()
      if (pos === undefined) {
        return
      }
      props.deleteNode()
      props.editor
        .chain()
        .insertContentAt(pos, {
          type: 'mention',
          attrs: {
            reference: order.reference.reference,
          },
        })
        .focus()
        .run()
    },
    [props]
  )

  const handleDocumentInsert = useCallback(
    (selectedIds: string[]): void => {
      isInsertingDocumentsRef.current = true
      setShowDocumentPicker(false)

      const pos = props.getPos()
      if (pos === undefined) {
        return
      }

      if (selectedIds.length === 1) {
        /* Single: inline mention */
        props.deleteNode()
        props.editor
          .chain()
          .insertContentAt(pos, {
            type: 'mention',
            attrs: { reference: `DocumentReference/${selectedIds[0]}` },
          })
          .focus()
          .run()
      } else {
        /* Multiple: bullet list */
        const $pos = props.editor.state.doc.resolve(pos)
        const listItemDepth = findEnclosingBulletListItemDepth($pos)
        const listItemFrom =
          listItemDepth !== null ? $pos.before(listItemDepth) : null
        const listItemTo =
          listItemDepth !== null ? $pos.after(listItemDepth) : null

        const listItems = selectedIds.map((id) => ({
          type: 'listItem',
          content: [
            {
              type: 'paragraph',
              content: [
                {
                  type: 'mention',
                  attrs: { reference: `DocumentReference/${id}` },
                },
              ],
            },
          ],
        }))

        if (listItemFrom !== null && listItemTo !== null) {
          /* Already in a bulletList – replace the current listItem with new ones */
          props.editor
            .chain()
            .insertContentAt({ from: listItemFrom, to: listItemTo }, listItems)
            .focus()
            .run()
        } else {
          props.deleteNode()
          props.editor
            .chain()
            .insertContentAt(pos, {
              type: 'bulletList',
              content: listItems,
            })
            .focus()
            .run()
        }
      }
    },
    [props]
  )

  const handleInlineConditionSelected = useCallback(
    (condition: ConditionPickerCondition): void => {
      if (!condition.diagnosis) {
        /* Shouldn't happen in practice. Mirror the onOpenChange teardown
           so the editor is left clean — the mention node is replaced by a
           plain @ the user can retrigger. */
        setInlineConditionSearch(null)
        const pos = props.getPos()
        if (pos === undefined) {
          return
        }
        props.deleteNode()
        props.editor.chain().insertContentAt(pos, '@').focus().run()
        return
      }
      const conditionRef = getReferenceString(condition.diagnosis.reference)
      // The chip pins to the frozen versioned snapshot; the encounter link
      // stays on the live unversioned reference (they are deliberately two
      // different lookups — #13981).
      const snapshot = getConditionMentionSnapshot(condition.diagnosis, {
        pinVersion,
      })
      /* Focus leaves the popover when we move the cursor past the node —
         Radix detects that and fires onOpenChange(false). Set the guard so
         the teardown doesn't delete the node we just committed. */
      isConfirmingInlineConditionRef.current = true
      setTimeout(() => {
        isConfirmingInlineConditionRef.current = false
      }, 0)
      props.updateAttributes({
        reference: snapshot.reference,
        display: `@${snapshot.name}`,
      })
      addConditions([conditionRef])
      setInlineConditionSearch(null)
      setOpen(false)
      const pos = props.getPos()
      if (pos === undefined) {
        return
      }
      props.editor.commands.focus(pos + props.node.nodeSize)
    },
    [props, addConditions, pinVersion]
  )

  return (
    <NodeViewWrapper as="span" className="inline">
      <Popover
        open={open}
        onOpenChange={(open) => {
          if (!open) {
            if (isOpeningOrderSheetRef.current) {
              isOpeningOrderSheetRef.current = false
              return
            }
            if (isOpeningDocumentPickerRef.current) {
              isOpeningDocumentPickerRef.current = false
              return
            }
            if (isSwitchingToInlineConditionRef.current) {
              isSwitchingToInlineConditionRef.current = false
              setOpen(true)
              return
            }
            if (isConfirmingInlineConditionRef.current) {
              isConfirmingInlineConditionRef.current = false
              return
            }
            const pos = props.getPos()
            if (pos === undefined) {
              return
            }
            props.deleteNode()
            props.editor.chain().insertContentAt(pos, '@').focus().run()
          } else {
            setOpen(true)
          }
        }}
      >
        <PopoverAnchor asChild>
          <span className="font-medium">@{displayString}</span>
        </PopoverAnchor>
        <PopoverContent
          align="start"
          className="w-[min(36rem,calc(100vw-2rem))] p-0"
          /* Portalled to document.body, so inside a suggestion island this
           * popover is outside the island's DOM subtree even though the
           * island's own editor opened it. The marker tells the island's
           * dismissal rules that focus (or a pointerdown) landing in here is
           * still the clinician working in the island. Inert everywhere else. */
          {...islandPortalAttributes}
        >
          {showObservationAutocomplete && (
            <ComboboxAsyncCommand<Coding>
              queryKey={['observation-autocomplete']}
              onSelect={handleObservationSelect}
              loadOptions={loadObservationOptions}
              getValue={(value) => value.code ?? ''}
              searchPlaceholder="Search observation…"
            />
          )}
          {!showObservationAutocomplete && inlineConditionSearch !== null && (
            <ConditionsCommand
              patientRef={currentParticipant?.reference}
              initialSearch={inlineConditionSearch}
              onConditionSelected={handleInlineConditionSelected}
            />
          )}
          {!showObservationAutocomplete && inlineConditionSearch === null && (
            <ComboboxCommand
              options={pickerOptions}
              fuseThreshold={0.4}
              groupBy={getConditionPairGroupKey}
              onSearchChange={setOrderSearch}
              // Note templates and other mention options almost always fuzzy-match
              // the search, which would otherwise suppress the condition-create row.
              alwaysShowCreate
              createLabel={
                existsCurrentParticipant
                  ? (search) => (
                      <div className="flex items-center gap-2">
                        <div className="p-0.5">
                          <IconClipboardPlus className="size-4 shrink-0" />
                        </div>
                        New condition: &ldquo;{search}&rdquo;
                      </div>
                    )
                  : undefined
              }
              onCreate={
                existsCurrentParticipant
                  ? (search) => {
                      isSwitchingToInlineConditionRef.current = true
                      setInlineConditionSearch(search)
                      /* Radix fires onOpenChange(false) synchronously during
                         the content swap, which consumes the ref. If it
                         doesn't (e.g., focus stays in the popover), clear on
                         the next tick so a later genuine close isn't
                         silently suppressed. */
                      setTimeout(() => {
                        isSwitchingToInlineConditionRef.current = false
                      }, 0)
                    }
                  : undefined
              }
              onSelect={async (value) => {
                if (value === 'signature-button') {
                  const pos = props.getPos()
                  if (pos === undefined) {
                    return
                  }
                  props.deleteNode()
                  props.editor
                    .chain()
                    .insertContentAt(pos, {
                      type: 'signature-button',
                      attrs: {
                        display: 'Sign',
                      },
                    })
                    .focus()
                    .run()
                  return
                }
                if (value === 'choice-button') {
                  const pos = props.getPos()
                  if (pos === undefined) {
                    return
                  }
                  props.deleteNode()
                  props.editor
                    .chain()
                    .insertContentAt(
                      pos,
                      "<button data-type='choice' data-options=''></button>"
                    )
                    .focus()
                    .run()
                  return
                }
                if (value === 'appointment-node') {
                  const pos = props.getPos()
                  if (pos === undefined) {
                    return
                  }
                  props.deleteNode()
                  props.editor
                    .chain()
                    .insertContentAt(pos, [
                      {
                        type: 'encounter',
                      },
                      { type: 'paragraph' },
                    ])
                    .focus()
                    .run()
                  return
                }
                if (value.startsWith('validated-tool/')) {
                  const toolName = value.split('/')[1]
                  const pos = props.getPos()
                  if (pos === undefined) {
                    return
                  }
                  props.deleteNode()
                  props.editor
                    .chain()
                    .insertContentAt(pos, [
                      { type: 'paragraph' },
                      {
                        type: 'validated-tool',
                        attrs: {
                          name: toolName,
                          response: null,
                          restorationId: generateUUID(),
                        },
                      },
                      { type: 'paragraph' },
                    ])
                    .setTextSelection(pos)
                    .focus()
                    .run()
                  return
                }
                if (value.startsWith('vital/')) {
                  const code = value.split('/')[1]
                  const vital = VITAL_OPTIONS.find(
                    (v) => codeFromCodeableConcept(v.coding) === code
                  )
                  if (vital) {
                    const coding = getCodingFromCodeableConcept(vital.coding)
                    invariant(coding, 'coding is required')
                    const pos = props.getPos()
                    if (pos === undefined) {
                      return
                    }
                    props.deleteNode()
                    props.editor
                      .chain()
                      .insertContentAt(pos, {
                        type: 'observation',
                        attrs: {
                          code: coding.code,
                          system: coding.system,
                          codingDisplay: coding.display,
                          scaleType: vital.scaleType,
                          unit: vital.unit,
                          reference: null,
                          display: coding.display,
                          answerOptions: vital.answerOptions ?? [],
                          autofocus: true,
                        },
                      })
                      .focus()
                      .run()
                  }
                  return
                }
                if (value === 'add-order') {
                  isOpeningOrderSheetRef.current = true
                  setOpen(false)
                  setShowOrderSheet(true)
                  return
                }
                if (value === 'create-task') {
                  const pos = props.getPos()
                  if (pos === undefined) {
                    return
                  }
                  props.deleteNode()

                  const store = useTaskComposerStore.getState()

                  store.setOnClose(() => {
                    props.editor.chain().setTextSelection(pos).focus().run()
                  })

                  store.setOnTaskCreated((task) => {
                    props.editor
                      .chain()
                      .focus()
                      .insertContentAt(pos, {
                        type: 'mention',
                        attrs: {
                          reference: task.reference.reference,
                        },
                      })
                      .run()
                  })

                  store.setOpen(true)
                  return
                }
                if (value === 'insert-documents') {
                  isOpeningDocumentPickerRef.current = true
                  setOpen(false)
                  setShowDocumentPicker(true)
                  return
                }
                if (value === 'observation-node') {
                  setShowObservationAutocomplete(true)
                  return
                }
                if (value === 'add-timeline-note-button') {
                  props.deleteNode()
                  props.editor
                    .chain()
                    .insertContentAt(props.editor.state.selection.anchor, {
                      type: 'add-timeline-note-button',
                      attrs: {
                        display: 'New chart note…',
                      },
                    })
                    .focus()
                    .run()
                  return
                }
                if (value === 'insert-conditions-button') {
                  const pos = props.getPos()
                  if (pos === undefined) {
                    return
                  }
                  props.deleteNode()
                  props.editor
                    .chain()
                    .insertContentAt(pos, {
                      type: 'insert-conditions-button',
                    })
                    .focus()
                    .run()
                  return
                }
                if (value === 'insert-current-conditions-button') {
                  const pos = props.getPos()
                  if (pos === undefined) {
                    return
                  }
                  props.deleteNode()
                  props.editor
                    .chain()
                    .insertContentAt(pos, {
                      type: 'insert-current-conditions-button',
                    })
                    .focus()
                    .run()
                  return
                }
                if (value === 'insert-current-conditions-with-notes-button') {
                  const pos = props.getPos()
                  if (pos === undefined) {
                    return
                  }
                  props.deleteNode()
                  props.editor
                    .chain()
                    .insertContentAt(pos, {
                      type: 'insert-current-conditions-with-notes-button',
                    })
                    .focus()
                    .run()
                  return
                }
                if (value === 'insert-medications-button') {
                  const pos = props.getPos()
                  if (pos === undefined) {
                    return
                  }
                  props.deleteNode()
                  props.editor
                    .chain()
                    .insertContentAt(pos, {
                      type: 'insert-medications-button',
                    })
                    .focus()
                    .run()
                  return
                }
                if (value === 'insert-allergies-button') {
                  const pos = props.getPos()
                  if (pos === undefined) {
                    return
                  }
                  props.deleteNode()
                  props.editor
                    .chain()
                    .insertContentAt(pos, {
                      type: 'insert-allergies-button',
                    })
                    .focus()
                    .run()
                  return
                }
                if (value === 'encounter-medications-block') {
                  const pos = props.getPos()
                  if (pos === undefined) {
                    return
                  }
                  props.deleteNode()
                  props.editor
                    .chain()
                    .insertContentAt(pos, [
                      {
                        type: 'encounter-medications-block',
                      },
                      { type: 'paragraph' },
                    ])
                    .focus()
                    .run()
                  return
                }
                if (value === 'conditions-addressed-block') {
                  const pos = props.getPos()
                  if (pos === undefined) {
                    return
                  }
                  props.deleteNode()
                  props.editor
                    .chain()
                    .insertContentAt(pos, [
                      {
                        type: 'conditions-addressed-block',
                      },
                      { type: 'paragraph' },
                    ])
                    .focus()
                    .run()
                  return
                }
                if (value === 'medication-refill-block') {
                  const pos = props.getPos()
                  if (pos === undefined) {
                    return
                  }
                  props.deleteNode()
                  props.editor
                    .chain()
                    .insertContentAt(pos, [
                      {
                        type: 'medication-refill-block',
                      },
                      { type: 'paragraph' },
                    ])
                    .focus()
                    .run()
                  return
                }
                if (value.startsWith(CONDITION_WITH_NOTE_PREFIX)) {
                  const conditionRefString = value.slice(
                    CONDITION_WITH_NOTE_PREFIX.length
                  )
                  const condition = orderedConditions.find(
                    (c) =>
                      getReferenceString(c.reference) === conditionRefString
                  )
                  if (!condition) {
                    return
                  }
                  const recentNote =
                    mostRecentNoteByConditionRef.get(conditionRefString)
                  // Chip pins to the frozen versioned snapshot; the encounter
                  // link stays on the live unversioned reference (#13981).
                  const snapshot = getConditionMentionSnapshot(condition, {
                    pinVersion,
                  })
                  props.updateAttributes({
                    reference: snapshot.reference,
                    display: `@${snapshot.name}`,
                  })
                  addConditions([conditionRefString])
                  setOpen(false)
                  const pos = props.getPos()
                  if (pos === undefined) {
                    return
                  }
                  const messageHtml = buildConditionNoteHtml(recentNote)
                  const $pos = props.editor.state.doc.resolve(pos)
                  const listItemDepth = findEnclosingBulletListItemDepth($pos)
                  if (listItemDepth !== null) {
                    props.editor
                      .chain()
                      .insertContentAt(
                        $pos.end(listItemDepth),
                        `<ul><li>${messageHtml}</li></ul>`
                      )
                      .focus()
                      .run()
                  } else {
                    props.editor
                      .chain()
                      .insertContentAt(pos + props.node.nodeSize, messageHtml)
                      .focus()
                      .run()
                  }
                  return
                }
                const instance =
                  orders?.find(
                    (order) => order.reference.reference === value
                  ) ??
                  (practitioners ?? []).find(
                    (practitioner) => practitioner.reference.reference === value
                  ) ??
                  noteTemplates?.find(
                    (noteTemplate) => noteTemplate.reference.reference === value
                  ) ??
                  orderTemplates?.find(
                    (orderTemplate) =>
                      orderTemplate.reference.reference === value
                  ) ??
                  participants.find(
                    (participant) => participant.reference.reference === value
                  ) ??
                  orderedConditions.find(
                    (condition) =>
                      getReferenceString(condition.reference) === value
                  ) ??
                  allergies?.find(
                    (allergy) => getReferenceString(allergy.reference) === value
                  ) ??
                  documentReferences?.find(
                    (docRef) => getReferenceString(docRef.reference) === value
                  ) ??
                  medications?.find(
                    (med) => getReferenceString(med.reference) === value
                  ) ??
                  validatedToolResponses?.find(
                    (response) =>
                      getReferenceString(response.reference) === value
                  ) ??
                  labObservations?.find(
                    (obs) => getReferenceString(obs.reference) === value
                  ) ??
                  interventions?.find(
                    (intervention) =>
                      getReferenceString(intervention.reference) === value
                  )
                if (instance instanceof ExtendedMedicationRequest) {
                  props.updateAttributes({
                    reference: value,
                    display: `@${instance.medicationDisplay ?? value}`,
                  })
                  setOpen(false)
                  const pos = props.getPos()
                  if (pos === undefined) {
                    return
                  }
                  props.editor.commands.focus(pos + props.node.nodeSize)
                } else if (instance instanceof OrderTemplate) {
                  const content = instance.sections
                    .map((section) => section.text?.div ?? '')
                    .join('\n')
                  if (content) {
                    props.deleteNode()
                    props.editor
                      .chain()
                      .insertContentAt(
                        props.editor.state.selection.anchor,
                        content
                      )
                      .focus()
                      .run()
                  }
                } else if (instance instanceof NoteTemplate) {
                  const pos = props.getPos()
                  if (pos === undefined) {
                    return
                  }
                  // Union the template's labels + categories into the host note
                  // before splicing its body. No-op outside a note page (e.g. the
                  // template editor), where the merge callback is undefined.
                  extension.options.onMergeTemplateMetadata?.(instance)
                  props.deleteNode()
                  props.editor
                    .chain()
                    .insertContentAt(pos, instance.data)
                    .focus()
                    .run()
                } else {
                  const pos = props.getPos()
                  if (pos === undefined) {
                    return
                  }
                  if (instance instanceof Order) {
                    // No display override: render the full OrderMention card
                    // (status icon + identifier + title), matching a pasted
                    // order link rather than collapsing to plain "@SR-###".
                    props.updateAttributes({
                      reference: value,
                    })
                  } else if (instance instanceof ExtendedPractitioner) {
                    props.updateAttributes({
                      reference: value,
                      display: `@${instance.name}`,
                    })
                  } else if (instance instanceof Participant) {
                    props.updateAttributes({
                      reference: value,
                      display: `@${instance.name}`,
                    })
                  } else if (instance instanceof Diagnosis) {
                    // Chip pins to the frozen versioned snapshot; the encounter
                    // link stays on the live unversioned reference (#13981).
                    const snapshot = getConditionMentionSnapshot(instance, {
                      pinVersion,
                    })
                    props.updateAttributes({
                      reference: snapshot.reference,
                      display: `@${snapshot.name}`,
                    })
                    addConditions([value])
                  } else if (instance instanceof ExtendedAllergy) {
                    props.updateAttributes({
                      reference: value,
                      display: `@${instance.name}`,
                    })
                  } else if (instance instanceof ExtendedDocumentReference) {
                    props.updateAttributes({
                      reference: value,
                      display: `@${instance.title}`,
                    })
                  } else if (instance instanceof ValidatedToolResponse) {
                    props.updateAttributes({
                      reference: value,
                      display: `@${instance.name}: ${instance.score} · ${formatDate(instance.authored)}`,
                    })
                  } else if (instance instanceof ExtendedObservation) {
                    props.updateAttributes({
                      reference: value,
                      display: `@${instance.display}: ${instance.valueDisplay ?? ''}${instance.effectiveDateTime ? ` · ${formatDate(instance.effectiveDateTime)}` : ''}`,
                    })
                  } else if (instance instanceof Intervention) {
                    /* No `@` prefix, unlike the mention subtypes around it.
                     * `display` round-trips as the anchor's own text, so it is
                     * what the stored note body reads as and what plain-text
                     * extraction picks up — a sigil belongs to the picker's
                     * input, not to the note's prose.
                     *
                     * The label only: timing, owner and status all change over
                     * the life of an intervention, and the chip re-derives from
                     * the live resource anyway. */
                    props.updateAttributes({
                      reference: value,
                      display: interventionLabel(instance),
                    })
                  }
                  setOpen(false)
                  props.editor.commands.focus(pos + props.node.nodeSize)
                }
              }}
            />
          )}
        </PopoverContent>
      </Popover>
      <Sheet
        open={showOrderSheet}
        onOpenChange={(open) => {
          if (!open) {
            if (isSubmittingOrderRef.current) {
              isSubmittingOrderRef.current = false
              return
            }
            setShowOrderSheet(false)
            const pos = props.getPos()
            if (pos === undefined) {
              return
            }
            props.deleteNode()
            props.editor.chain().insertContentAt(pos, '@').focus().run()
          }
        }}
      >
        <SheetContent
          hideClose
          side="right"
          className="max-w-2xl"
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          <OrderForm
            onCancel={() => setShowOrderSheet(false)}
            onSubmit={handleOrderSubmit}
          />
        </SheetContent>
      </Sheet>
      <DocumentPickerDialog
        open={showDocumentPicker}
        onOpenChange={(open) => {
          if (!open) {
            if (isInsertingDocumentsRef.current) {
              isInsertingDocumentsRef.current = false
              return
            }
            setShowDocumentPicker(false)
            const pos = props.getPos()
            if (pos === undefined) {
              return
            }
            props.deleteNode()
            props.editor.chain().insertContentAt(pos, '@').focus().run()
          }
        }}
        documents={pickerDocuments}
        onInsert={handleDocumentInsert}
      />
    </NodeViewWrapper>
  )
}
